import type { JSX } from '@builder.io/qwik';
import { component$, useContext } from '@builder.io/qwik';
import { cva } from 'class-variance-authority';
import {
  getCSSColorText,
  getCSSSectionHeader,
  getCSSSizeLgText,
  getCSSSizeMdText,
  getCSSSizeSmText,
} from '~/utilities/getCSS';
import { AppDataContext } from '~/contexts/AppDataContext';
import { getFontSizeObject } from '~/utilities/getFontSizeObject';
import { getSectionHeaderObject } from '~/utilities/getSectionHeaderObject';
import { ArticleContext } from '~/contexts/ArticleContext';
import JsonLd from '~/components/Shared/Utility/JsonLd/JsonLd';
import CardImage from '~/components/Layout/Body/Page/CardImage';
import CardText from '~/components/Layout/Body/Page/CardText';

const sectionCSS = cva('py-2.5', {
  variants: {
    ...getCSSSectionHeader(),
  },
});

const headingCSS = cva('mb-4', {
  variants: {
    color: getCSSColorText(),
    sizeSm: getCSSSizeSmText(),
    sizeMd: getCSSSizeMdText(),
    sizeLg: getCSSSizeLgText(),
  },
});

export default component$((): JSX.Element => {
  const {
    themeComponents: { relatedPosts, sectionHeader },
    payloadToSSR: { pageContent },
  } = useContext(AppDataContext);

  const article = useContext(ArticleContext);
  const related = pageContent.relatedPosts?.postsList.value.articles;

  if (!related || !related.length || article?.finiteScrollNumber !== 0)
    return <></>;

  return (
    <aside
      class={sectionCSS({
        ...getSectionHeaderObject(sectionHeader),
      })}
    >
      <h2
        class={headingCSS({
          color: relatedPosts.titleColor.value,
          ...getFontSizeObject(relatedPosts, 'titleFontSize'),
        })}
      >
        {pageContent.relatedPosts?.title.value || 'Related Posts'}
      </h2>
      <div class='flex flex-col gap-4'>
        {related.map((item) => {
          const { title, articleUrl, cover, seoDescription } = item;

          return (
            <article
              key={item.articleUrl}
              class='card relative box-border group/card flex flex-row gap-2'
            >
              <JsonLd type='articleEach' data={item} />
              <CardImage
                cover={cover}
                coverType='image'
                imageWidths={{
                  small: 100,
                  medium: 100,
                  large: 100,
                }}
                isSingle={false}
                imageAspectRatio='1/1'
                wrapperClass='relative min-w-[100px]'
              />
              <CardText
                title={title}
                description={seoDescription}
                showDescription={false}
                showAuthor={false}
                showDate={false}
                showImage={true}
                showMainCategory={false}
                isSingle={true}
                titleSize='normal'
                wrapperClass=''
              />
              <a
                href={articleUrl}
                class='absolute inset-0 w-full h-full'
                title={title}
              />
            </article>
          );
        })}
      </div>
    </aside>
  );
});
